import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'

interface PageHeaderProps {
  /**
   * Badge text to display at the top
   */
  badgeText?: string
  /**
   * Badge icon (from Hero Icons)
   */
  badgeIcon?: React.ReactNode
  /**
   * Main page title
   */
  title: string
  /**
   * Subtitle/description text
   */
  subtitle?: string
  /**
   * Custom badge background color (defaults to sun)
   */
  badgeColor?: string
  /**
   * Custom badge text color (defaults to offBlack)
   */
  badgeTextColor?: string
  /**
   * Custom title color (defaults to white)
   */
  titleColor?: string
  /**
   * Custom subtitle color (defaults to thunderGrey)
   */
  subtitleColor?: string
  /**
   * Optional className for the container
   */
  className?: string
  /**
   * Optional action buttons to display on the right side
   */
  actions?: React.ReactNode
  /** Shown right after the title on the same line, such as a beta tag. */
  titleAdornment?: React.ReactNode
}

/**
 * PageHeader Component - Standardized header for all pages
 *
 * Follows the design pattern from GameCenterPage:
 * - Badge: Pill-shaped (borderRadius: 20px), sun background, offBlack text
 * - Title: 32px, bold (700), white
 * - Subtitle: 16px, thunderGrey
 *
 * @example
 * ```tsx
 * <PageHeader
 *   badgeText="GAME CENTER"
 *   title="Welcome to the Game Center"
 *   subtitle="Choose how you want to start your gaming experience"
 * />
 * ```
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  titleColor = INTERAX_COLORS.white,
  subtitleColor = INTERAX_COLORS.thunderGrey,
  className = '',
  actions,
  titleAdornment
}) => {
  return (
    <Box sx={{ mb: 4 }} className={className}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: subtitle ? 1 : 0 }}>
            <Box component="h1" sx={{ fontSize: '32px', fontWeight: 700, color: titleColor, m: 0 }}>
              {title}
            </Box>

            {titleAdornment}
          </Box>

          {/* Subtitle */}
          {subtitle && (
            <Box
              component="p"
              sx={{
                fontSize: '16px',
                color: subtitleColor,
                maxWidth: '600px'
              }}
            >
              {subtitle}
            </Box>
          )}
        </Box>

        {/* Actions (optional buttons on the right) */}
        {actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  )
}
